import type { Cacheable, GetTokenSilentlyOptions, ICache } from '@auth0/auth0-spa-js'
import {
  Auth0Client,
  GenericError,
  LocalStorageCache,
  MfaRequiredError,
  MissingRefreshTokenError,
  TimeoutError,
} from '@auth0/auth0-spa-js'
import { version as AUTH0_SPA_JS_VERSION } from '@auth0/auth0-spa-js/package.json'
import uuid from '@openphone/lib/uuid'
import type { AuthStoreUserContext, LogoutReason } from '@openphone/quo-observability'
import { trackCustomAction } from '@openphone/quo-observability'
import isNetworkError from 'is-network-error'
import Cookies from 'js-cookie'
import { parsePhoneNumber } from 'libphonenumber-js'
import { action, makeAutoObservable, runInAction, when } from 'mobx'
import { matchPath } from 'react-router-dom'

import config, { platform, deviceId } from '@src/config'
import { createPromiseWithResolvers } from '@src/lib/PromiseWithResolvers'
import StatefulPromise from '@src/lib/StatefulPromise'
import { logError } from '@src/lib/log'

import type Service from '.'
import makePersistable from './storage/makePersistable'
import type { Invite } from './transport/account'
import isClockSkewError from './transport/isClockSkewError'

// https://github.com/auth0/docs/blob/master/articles/api/authentication/errors/_errors.md
const AUTH0_BACKEND_ERRORS = [
  'invalid_grant',
  'login_required',
  'invalid_request',
  'unauthorized',
  'password_leaked',
  'unauthorized_client',
  'requires_validation',
  'access_denied',
  'too_many_requests',
  'requires_verification',
  'interaction_required',
  'consent_required',
]

const RETRY_TIMEOUT_MS = 30_000
const RETRY_BACKOFF_MS = 100

const isUniversalLoginUnrecoverableError = (error: unknown): boolean => {
  if (error instanceof MfaRequiredError || error instanceof MissingRefreshTokenError) {
    return true
  }

  // checks for a list of errors that can be thrown by the auth0 backend
  if (error instanceof GenericError && AUTH0_BACKEND_ERRORS.includes(error.error)) {
    return true
  }

  // an error thrown from the auth0 SDK
  if (isClockSkewError(error)) {
    return true
  }

  return false
}

type TokenRefreshResult = {
  token: string
  release: (reason: 'new-token-succeeded' | 'new-token-also-failed') => void
}

type RefreshUnloadMarker = {
  attemptId: string
  startedAt: number
  unloadAt: number
  kind: 'pagehide' | 'visibilityHidden'
}

type IdTokenClaims = {
  op_user_id?: string
  op_org_id?: string
  sub?: string
}

const REFRESH_UNLOAD_MARKER_KEY = 'auth0RefreshUnloadMarker'
/** Markers older than this are ignored — they're likely from a long-prior session. */
const REFRESH_UNLOAD_MARKER_MAX_AGE_MS = 24 * 60 * 60 * 1000

type RefreshOutcome =
  | 'success'
  | 'timeout'
  | 'network'
  | 'mfa_required'
  | 'missing_refresh_token'
  | 'invalid_grant'
  | 'other_backend_error'
  | 'unknown'

const classifyRefreshError = (
  err: unknown,
): { outcome: RefreshOutcome; errorCode: string | null } => {
  if (err instanceof TimeoutError) {
    return { outcome: 'timeout', errorCode: null }
  }
  if (err instanceof MfaRequiredError) {
    return { outcome: 'mfa_required', errorCode: null }
  }
  if (err instanceof MissingRefreshTokenError) {
    return { outcome: 'missing_refresh_token', errorCode: null }
  }
  if (err instanceof GenericError) {
    if (err.error === 'invalid_grant') {
      return { outcome: 'invalid_grant', errorCode: err.error }
    }
    if (AUTH0_BACKEND_ERRORS.includes(err.error)) {
      return { outcome: 'other_backend_error', errorCode: err.error }
    }
    return { outcome: 'unknown', errorCode: err.error }
  }
  if (isNetworkError(err)) {
    return { outcome: 'network', errorCode: null }
  }
  return { outcome: 'unknown', errorCode: null }
}

class UniversalLoginAuthStore {
  invite: Invite | null = null
  inviteToken: string | null = null
  referralCode: string | null = null
  isAuthenticated = false
  isInitializing = false
  isImpersonating = false
  auth0Client: Auth0Client

  private _accessTokenOverride: string | null = null

  /**
   * A timestamp when the session was started.
   * Used to determine when the user should be logged out.
   * The maximum session lifetime comes from the
   * webAuthMaxSessionLifetimeDays flag (see isSessionAboutToExpire).
   */
  sessionStartedOn: number | null = null
  /**
   * A flag that indicates if the user should see the modal
   * with the reason for the logout.
   */
  logoutReason: 'machine-clock-skew' | null = null

  /**
   * A phone number that a user selected before the signup.
   * Used to preselect the phone number during the onboarding
   */
  private _phoneNumber: string | null = null

  /**
   * Stores pending signup tracking info when user authenticates via login flow.
   * Used to emit signup event in onboarding if user turns out to be new.
   */
  pendingSignupViaLogin: {
    method: 'passwordless' | 'google' | 'apple'
    trackGTM: boolean
  } | null = null

  private acceptInvitePromise = new StatefulPromise(this.handleAcceptInvite.bind(this))

  /**
   * Stores the most recently used access token
   * Used to detect when a token refresh occurs
   */
  private _previousAccessToken: string | null = null

  /**
   * Simple promise-chain mutex for force refresh.
   * Each caller awaits the previous, preventing concurrent force refreshes.
   */
  private _refreshMutex: Promise<void> = Promise.resolve()

  // Lifecycle / refresh-attempt tracking. Used to diagnose whether refresh failures
  // correlate with page/tab unloads that aborted an in-flight token exchange.
  private _lastPageHideAt: number | null = null
  private _lastVisibilityHiddenAt: number | null = null
  private _currentRefreshStartedAt: number | null = null
  private _currentRefreshAttemptId: string | null = null

  // Reference shared with Auth0Client's `auth0Client.env` option. Auth0 re-reads
  // and base64-encodes this object into the Auth0-Client header on every
  // /oauth/token request, so mutating it after we learn the user's identity
  // surfaces userId/orgId in Auth0 logs as @data.auth0_client.env.{userId,orgId}.
  private _auth0ClientEnv: {
    deviceId: string
    appVersion: string
    userId?: string
    orgId?: string
    auth0UserId?: string
  } = { deviceId, appVersion: config.VERSION }

  constructor(private service: Service) {
    makeAutoObservable(
      this,
      { auth0Client: false, isSessionAboutToExpire: false, setTokenOverride: action },
      { autoBind: true },
    )

    makePersistable(this, 'UniversalLoginAuthStore', {
      sessionStartedOn: this.service.storage.sync(),
      logoutReason: this.service.storage.sync(),
      inviteToken: this.service.storage.sync(),
      referralCode: this.service.storage.sync(),
      _accessTokenOverride: this.service.storage.sync(),
      isImpersonating: this.service.storage.sync(),
    })

    const isElectron = Boolean(this.service.desktopVersion)
    const sharedAuthCookieDomain = getSharedAuthCookieDomain()
    this.auth0Client = new Auth0Client({
      // we need to use cookies for transactions in the electron app
      // since authentication is done in a separate tab
      useCookiesForTransactions: isElectron,
      authorizationParams: {
        scope: 'openid profile email offline_access',
        audience: config.AUTH0_UNIVERSAL_LOGIN_AUDIENCE,

        redirect_uri: isElectron
          ? `${window.location.origin}/native/login`
          : `${window.location.origin}/auth/authorize`,
        opApp: 'web',
        opAppVersion: config.VERSION,
        opDevice: platform ?? 'browser',
        opDeviceId: deviceId,
        opDesktopVersion: this.service.desktopVersion,
        // this is only used to facilitate universal login with a local backend.
        // The backend ignores it unless it's the dev tenant with an `openphone.co` email
        identityUrl: config.IDENTITY_OVERRIDE_URL,
      },
      domain: config.AUTH0_UNIVERSAL_LOGIN_DOMAIN,
      clientId: config.AUTH0_UNIVERSAL_LOGIN_CLIENT_ID,
      sessionCheckExpiryDays: 2,
      useRefreshTokens: true,
      cache: sharedAuthCookieDomain
        ? new SubdomainSharedCache(sharedAuthCookieDomain)
        : new TrackingCache(config.AUTH0_UNIVERSAL_LOGIN_CLIENT_ID),
      httpTimeoutInSeconds: 60,
      authorizeTimeoutInSeconds: 120,
      // env is base64-JSON-encoded into the Auth0-Client header on /oauth/token,
      // so the user/device info will surface in datadog logs
      auth0Client: {
        name: 'auth0-spa-js',
        version: AUTH0_SPA_JS_VERSION,
        env: this._auth0ClientEnv,
      },
    })

    this.installLifecycleListeners()
  }

  private installLifecycleListeners() {
    if (typeof window === 'undefined') {
      return
    }

    window.addEventListener('pagehide', () => {
      const now = Date.now()
      this._lastPageHideAt = now
      this.recordUnloadDuringRefresh(now, 'pagehide')
    })

    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState !== 'hidden') {
          return
        }
        const now = Date.now()
        this._lastVisibilityHiddenAt = now
        this.recordUnloadDuringRefresh(now, 'visibilityHidden')
      })
    }

    // 'storage' fires only for changes made by other tabs/windows on this
    // origin, so a hit here means something outside this tab took the
    // refresh token away.
    window.addEventListener('storage', (event) => {
      if (!this.isAuthenticated) {
        return
      }
      if (event.key === null) {
        // localStorage.clear() ran in another tab. A full clear carries no
        // per-key detail, so we report the coarser 'external-clear' change
        // with a sentinel key to keep `key` uniformly queryable.
        trackCustomAction({
          type: 'universal-login-refresh-token-cache-change',
          change: 'external-clear',
          key: '(localStorage.clear)',
        })
        return
      }
      if (!event.key.startsWith(AUTH0_CACHE_KEY_PREFIX)) {
        return
      }
      const hadRefreshToken = event.oldValue?.includes('"refresh_token"') ?? false
      const hasRefreshToken = event.newValue?.includes('"refresh_token"') ?? false
      if (hadRefreshToken && !hasRefreshToken) {
        trackCustomAction({
          type: 'universal-login-refresh-token-cache-change',
          change: 'external',
          key: event.key,
        })
      }
    })
  }

  private recordUnloadDuringRefresh(
    unloadAt: number,
    kind: 'pagehide' | 'visibilityHidden',
  ) {
    const attemptId = this._currentRefreshAttemptId
    const startedAt = this._currentRefreshStartedAt
    if (!attemptId || startedAt == null) {
      return
    }

    try {
      const marker: RefreshUnloadMarker = { attemptId, startedAt, unloadAt, kind }
      window.localStorage.setItem(REFRESH_UNLOAD_MARKER_KEY, JSON.stringify(marker))
    } catch (error) {
      logError(error)
    }
  }

  /**
   * Read the unload marker without removing it. We only clear the marker once we
   * know an actual refresh happened (or errored) — otherwise repeated cache hits
   * between an interrupted refresh and the next real one would silently consume
   * the marker without ever surfacing it in telemetry.
   */
  private peekUnloadMarker(): RefreshUnloadMarker | null {
    try {
      const raw = window.localStorage.getItem(REFRESH_UNLOAD_MARKER_KEY)
      if (!raw) {
        return null
      }
      const parsed = JSON.parse(raw) as RefreshUnloadMarker
      if (
        typeof parsed?.attemptId !== 'string' ||
        typeof parsed?.startedAt !== 'number' ||
        typeof parsed?.unloadAt !== 'number'
      ) {
        return null
      }
      if (Date.now() - parsed.unloadAt > REFRESH_UNLOAD_MARKER_MAX_AGE_MS) {
        // stale — clean it up so it doesn't linger
        this.clearUnloadMarker()
        return null
      }
      return parsed
    } catch (error) {
      logError(error)
      return null
    }
  }

  private clearUnloadMarker() {
    try {
      window.localStorage.removeItem(REFRESH_UNLOAD_MARKER_KEY)
    } catch (error) {
      logError(error)
    }
  }

  async init() {
    if (this._accessTokenOverride) {
      // Override hydrated from storage — we're still authenticated via the override.
      runInAction(() => {
        this.isAuthenticated = true
      })
      return
    }
    // we need to skip the auth check until the user completes the authentication flow
    if (!matchPath('/auth/*', window.location.pathname)) {
      runInAction(() => {
        this.isInitializing = true
      })
      try {
        await this.checkIfAuthenticated()
      } finally {
        runInAction(() => {
          this.isInitializing = false
        })
      }
    }
  }

  setTokenOverride(token: string, impersonating: boolean) {
    this._accessTokenOverride = token
    this.isAuthenticated = true
    this.isImpersonating = impersonating
  }

  /**
   * Entry point for a token-override session. No-op if the token matches the persisted
   * one (re-entry via the same URL); otherwise wipes Auth0's cache and all local state
   * before installing the new override.
   */
  async applyTokenOverrideFromUrl(token: string, impersonating: boolean) {
    if (this._accessTokenOverride === token) {
      return
    }
    await this.auth0Client.logout({ openUrl: false })
    await this.service.storage.clearAll()
    this.setTokenOverride(token, impersonating)
  }

  acceptInviteIfPossible() {
    if (!this.isAuthenticated || !this.inviteToken) {
      return Promise.resolve()
    }

    return this.acceptInvite(this.inviteToken)
  }

  acceptReferralIfPossible() {
    if (!this.isAuthenticated || !this.referralCode) {
      return Promise.resolve()
    }

    return this.acceptReferral(this.referralCode)
  }

  hideLogoutReason() {
    this.logoutReason = null
  }

  showLogoutReason(reason: 'machine-clock-skew') {
    this.logoutReason = reason
  }

  async logout(reason?: 'machine-clock-skew') {
    if (reason) {
      this.showLogoutReason(reason)
    }

    if (this._accessTokenOverride) {
      // In an override session there's no Auth0 session to log out of; just wipe
      // local state and send the user to /login for a fresh Auth0 flow.
      await this.service.storage.clearAll()
      window.location.href = '/login'
      return
    }

    return this.auth0Client.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login`,
      },
    })
  }

  async getAccessToken(): Promise<string> {
    if (this._accessTokenOverride) {
      return this._accessTokenOverride
    }

    if (!this.isAuthenticated) {
      return ''
    }

    return await this.getAccessTokenWithRetry({})
  }

  /**
   * `getAccessToken` should handle the normal case of refreshing automatically. Callers should ONLY
   * use this if they receive a 401 error with the token from `getAccessToken`.
   *
   * By default the Auth0 client will refresh if the token is about to expire, but we have a
   * mechanism in the backend to invalidate tokens on e.g. org change. In those cases, we would get
   * a 401 from a token that is not expired. In those cases, the caller can use this to get a new
   * token. Since we're forcing the refresh, we use a mutex to prevent concurrent requests.
   *
   * The caller MUST release the lock after retrying the request. If the call succeeds, the lock is
   * released with `'new-token-succeeded'` and we proceed as normal. If they receive a 401 again, then
   * the lock is released with `'new-token-also-failed'` and we log the user out.
   */
  forceRefreshAccessToken(): Promise<TokenRefreshResult> {
    if (this._accessTokenOverride) {
      void this.handleRefreshTokenErrorLogout('token-override-rejected')
      return Promise.resolve({ token: '', release: () => {} })
    }

    const capturedToken = this._previousAccessToken

    const lock = createPromiseWithResolvers<void>()
    const result = createPromiseWithResolvers<TokenRefreshResult>()

    this._refreshMutex = this._refreshMutex.then(async () => {
      // Another caller already refreshed while we waited
      if (capturedToken !== this._previousAccessToken && this._previousAccessToken) {
        result.resolve({
          token: this._previousAccessToken,
          release: () => {},
        })
        return
      }

      // Delegate to getAccessTokenWithRetry so transient errors (TimeoutError,
      // network blips) are retried instead of immediately logging the user out.
      // On final failure it logs the user out and throws; the redirect to /login
      // ends execution before the throw can be observed in practice.
      const token = await this.getAccessTokenWithRetry(
        { cacheMode: 'off' },
        'forceRefresh',
      )

      // Capture user context now, while the id-token cache is still populated.
      // The release callback may fire after the SDK cache is cleared by logout.
      const userContext = await this.getUserContext()

      let released = false
      result.resolve({
        token,
        release: (reason) => {
          if (released) {
            return
          }
          released = true
          if (reason !== 'new-token-succeeded') {
            const err = new Error('Token rejected after force refresh')
            logError(err)
            trackCustomAction({
              type: 'multiple-rejected-tokens',
              error: err,
              ...userContext,
            })
            void this.handleRefreshTokenErrorLogout('api-rejected-fresh-token')
          }
          lock.resolve()
        },
      })

      // Hold the mutex until the caller releases
      await lock.promise
    })

    return result.promise
  }

  /**
   * Wraps a single call to `auth0Client.getTokenSilently` with refresh-attempt
   * bookkeeping. Each retry inside `getAccessTokenWithRetry` calls this once, with
   * the loop index passed as `retryAttempt`.
   *
   * Telemetry is emitted only when something interesting happens:
   *   - the access token actually changed (a real refresh against Auth0), or
   *   - the call errored.
   * Cache hits — by far the majority — emit nothing.
   *
   * The unload marker is peeked (not consumed) so it survives any number of
   * cache hits between an interrupted refresh and the next real refresh; it's
   * only cleared when we actually emit telemetry that references it.
   */
  private async getTokenSilentlyWithTelemetry(
    options: GetTokenSilentlyOptions,
    caller: 'getAccessToken' | 'forceRefresh' | 'checkIfAuthenticated',
    retryAttempt: number,
  ): Promise<string> {
    const attemptId = uuid()
    const startedAt = Date.now()
    const marker = this.peekUnloadMarker()

    this._currentRefreshAttemptId = attemptId
    this._currentRefreshStartedAt = startedAt

    const emit = async (outcome: RefreshOutcome, errorCode: string | null) => {
      if (marker) {
        this.clearUnloadMarker()
      }
      const userContext = await this.getUserContext()
      trackCustomAction({
        type: 'universal-login-token-refresh-result',
        attemptId,
        caller,
        cacheMode: options.cacheMode ?? 'on',
        outcome,
        errorCode,
        durationMs: Date.now() - startedAt,
        retryAttempt,
        msSinceLastPageHide:
          this._lastPageHideAt != null ? startedAt - this._lastPageHideAt : null,
        msSinceLastVisibilityHidden:
          this._lastVisibilityHiddenAt != null
            ? startedAt - this._lastVisibilityHiddenAt
            : null,
        previousAttemptId: marker?.attemptId ?? null,
        previousAttemptAbortedMsAgo: marker ? startedAt - marker.unloadAt : null,
        previousAttemptAbortKind: marker?.kind ?? null,
        ...userContext,
      })
    }

    try {
      const token = await this.auth0Client.getTokenSilently(options)
      const tokenChanged =
        !!this._previousAccessToken && this._previousAccessToken !== token
      if (tokenChanged) {
        await emit('success', null)
      }
      return token
    } catch (err) {
      const { outcome, errorCode } = classifyRefreshError(err)
      if (err instanceof MissingRefreshTokenError) {
        this.trackMissingRefreshTokenDiagnostic(caller)
      }
      await emit(outcome, errorCode)
      throw err
    } finally {
      if (this._currentRefreshAttemptId === attemptId) {
        this._currentRefreshAttemptId = null
        this._currentRefreshStartedAt = null
      }
    }
  }

  private async getAccessTokenWithRetry(
    options: GetTokenSilentlyOptions,
    caller: 'getAccessToken' | 'forceRefresh' = 'getAccessToken',
  ): Promise<string> {
    let deadline = Date.now() + RETRY_TIMEOUT_MS
    let retryAttempt = 0
    let lastAttempt = Date.now()

    // If the machine is suspended mid-loop, the `deadline` will have passed without
    // actually retrying, so restart the clock.
    const restartClockIfSuspended = () => {
      if (Date.now() - lastAttempt > RETRY_TIMEOUT_MS) {
        deadline = Date.now() + RETRY_TIMEOUT_MS
      }
      lastAttempt = Date.now()
    }

    while (true) {
      restartClockIfSuspended()
      try {
        const token = await this.getTokenSilentlyWithTelemetry(
          options,
          caller,
          retryAttempt,
        )

        if (this._previousAccessToken && this._previousAccessToken !== token) {
          const userContext = await this.getUserContext()
          trackCustomAction({
            type: 'universal-login-token-refresh',
            did_succeed: true,
            ...userContext,
          })
          void this.service.transport.handleRefreshAuthToken(token)
        }

        runInAction(() => {
          this._previousAccessToken = token
        })

        return token
      } catch (err) {
        logError(err)

        if (isUniversalLoginUnrecoverableError(err)) {
          if (isClockSkewError(err)) {
            this.showLogoutReason('machine-clock-skew')
          }

          const userContext = await this.getUserContext()
          trackCustomAction({
            type: 'universal-login-token-refresh',
            did_succeed: false,
            ...userContext,
          })

          await this.handleRefreshTokenErrorLogout('refresh-token-unrecoverable')
          return ''
        }

        // If the device is offline, suspend retries until we have a connection
        // and reset the clock
        if (isNetworkError(err) && !this.service.transport.connectivity.online) {
          retryAttempt += 1
          const userContext = await this.getUserContext()
          trackCustomAction({
            type: 'universal-login-token-refresh-offline-park',
            ...userContext,
          })
          // wait until the device is back online and reset the clock
          await when(() => this.service.transport.connectivity.online)
          deadline = Date.now() + RETRY_TIMEOUT_MS
          continue
        }

        const isRetryable = err instanceof TimeoutError || isNetworkError(err)

        if (isRetryable) {
          // A network error returns ~immediately, so a large gap across this attempt
          // means the machine slept while the request was in flight
          if (isNetworkError(err)) {
            restartClockIfSuspended()
          }
          if (Date.now() < deadline) {
            retryAttempt += 1
            await new Promise((r) => setTimeout(r, RETRY_BACKOFF_MS))
            continue
          }
        }

        if (err instanceof Error) {
          const userContext = await this.getUserContext()
          trackCustomAction({
            type: 'universal-login-token-exchange-failure',
            error: err,
            ...userContext,
          })
        }

        // Either unretryable or we exhausted the retry budget. Log the user out.
        await this.handleRefreshTokenErrorLogout('refresh-token-retry-exhausted')

        // the call above will redirect to the login page, so we will never
        // actually reach this, but make the compiler happy
        throw err
      }
    }
  }

  /**
   * Fetches the user identity that's attached to every telemetry event emitted from
   * this store. Reads from the SDK's id-token cache via `getUser()`; returns nulls
   * if the user is not yet authenticated.
   */
  private async getUserContext(): Promise<AuthStoreUserContext> {
    try {
      const user = await this.auth0Client.getUser<IdTokenClaims>()
      const context = {
        userId: user?.op_user_id ?? null,
        orgId: user?.op_org_id ?? null,
        auth0UserId: user?.sub ?? null,
      }
      this._auth0ClientEnv.userId = context.userId ?? undefined
      this._auth0ClientEnv.orgId = context.orgId ?? undefined
      this._auth0ClientEnv.auth0UserId = context.auth0UserId ?? undefined
      return context
    } catch (error) {
      logError(error)
      return { userId: null, orgId: null, auth0UserId: null }
    }
  }

  /**
   * Snapshots the Auth0 localStorage cache at the moment a refresh fails with
   * MissingRefreshTokenError, to distinguish "entry gone entirely" from "entry
   * present but stripped of its refresh token".
   */
  private trackMissingRefreshTokenDiagnostic(
    caller: 'getAccessToken' | 'forceRefresh' | 'checkIfAuthenticated',
  ) {
    try {
      const auth0Keys = Object.keys(window.localStorage).filter((key) =>
        key.startsWith(AUTH0_CACHE_KEY_PREFIX),
      )
      const entryKey = auth0Keys.find(
        (key) =>
          key.includes(`::${config.AUTH0_UNIVERSAL_LOGIN_CLIENT_ID}::`) &&
          !key.endsWith(AUTH0_USER_INFO_CACHE_SUFFIX),
      )
      const entry = entryKey ? window.localStorage.getItem(entryKey) : null
      void this.getUserContext().then((userContext) =>
        trackCustomAction({
          type: 'universal-login-missing-refresh-token-diagnostic',
          caller,
          auth0CacheKeyCount: auth0Keys.length,
          cacheEntryExists: entry != null,
          cacheEntryHasRefreshToken: entry?.includes('"refresh_token"') ?? false,
          ...userContext,
        }),
      )
    } catch (error) {
      logError(error)
    }
  }

  private handleRefreshTokenErrorLogout(reason: LogoutReason) {
    runInAction(() => {
      this.isAuthenticated = false
    })

    return this.service.clearAllAndRestart({ reason }).catch(logError)
  }

  async checkIfAuthenticated(): Promise<boolean> {
    if (this._accessTokenOverride) {
      runInAction(() => {
        this.isAuthenticated = true
      })
      return true
    }
    try {
      await this.getTokenSilentlyWithTelemetry({}, 'checkIfAuthenticated', 0)
      runInAction(() => {
        this.isAuthenticated = true
      })
      const userContext = await this.getUserContext()
      trackCustomAction({
        type: 'universal-login-check-if-authenticated-success',
        ...userContext,
      })
    } catch (err) {
      const userContext = await this.getUserContext()
      if (isUniversalLoginUnrecoverableError(err)) {
        // if auth0 deems that the user needs to reauthenticate,
        // and we have not reset their data yet,
        // we initiate a reset to ensure that they always have a fresh start
        this.service.storage.resetDatabaseIfNecessary().catch(logError)
        this.service.storage.resetLocalStorage()

        logError(err)

        runInAction(() => {
          this.isAuthenticated = false
        })
        trackCustomAction({
          type: 'universal-login-check-if-authenticated-error',
          error: err as Error,
          ...userContext,
        })
      } else {
        trackCustomAction({
          type: 'universal-login-check-if-authenticated-unknown-error',
          error: err as Error,
          ...userContext,
        })
        // if it's not known Universal Login error, then we assume it's due to a network error
        // or something else...
        //
        // when the user is making the next API call, they will get logged out if their token is invalid,
        // so it's safe to keep them logged in to allow read-only access to the app
        runInAction(() => {
          this.isAuthenticated = true
        })
      }
    }

    return this.isAuthenticated
  }

  fetchAndSetInvite(token: string) {
    this.inviteToken = token
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- UXP-3744 - Fix Promise-related ESLint issues
    this.service.user.fetchInviteByToken(token).then(
      action((invite) => {
        this.invite = invite
      }),
    )
  }

  /**
   * Check if the session is about to expire.
   * If the current day is within 5 days of the session expiration date,
   * we consider the session as about to expire.
   *
   * The session's maximum age is Auth0's per-application Maximum Refresh
   * Token Lifetime, which the client can't read from Auth0 — the
   * webAuthMaxSessionLifetimeDays flag mirrors it and must be kept in sync
   * with the tenant setting.
   */
  get isSessionAboutToExpire(): boolean {
    if (!this.sessionStartedOn) {
      return false
    }

    const dayInMilliseconds = 1000 * 60 * 60 * 24
    const fiveDaysInMilliseconds = 5 * dayInMilliseconds
    const maxLifetimeDays = this.service.flags.flags.webAuthMaxSessionLifetimeDays
    const sessionExpiryTime = this.sessionStartedOn + maxLifetimeDays * dayInMilliseconds
    const timeUntilExpiry = sessionExpiryTime - new Date().getTime()

    // session is about to expire if there are 5 days or less remaining and it hasn't expired yet
    return timeUntilExpiry <= fiveDaysInMilliseconds && timeUntilExpiry > 0
  }

  get isAcceptingInvite() {
    return this.acceptInvitePromise.status === 'loading'
  }

  get phoneNumber(): string | null {
    return this._phoneNumber
  }

  set phoneNumber(phoneNumber: string) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      const parsedPhoneNumber = parsePhoneNumber(phoneNumber)

      if (parsedPhoneNumber?.isValid()) {
        this._phoneNumber = phoneNumber
      }
    } catch (error) {
      logError(error)
      this._phoneNumber = null
    }
  }

  async completeAuthentication() {
    await this.checkIfAuthenticated()
    await this.acceptInviteIfPossible().catch(logError)
    await this.acceptReferralIfPossible().catch(logError)
  }

  private async handleAcceptInvite(token: string) {
    await this.service.transport.account.invites.accept(token)

    runInAction(() => {
      this.inviteToken = null
    })

    // removing all the query params
    window.location.replace(location.pathname)
  }

  private acceptReferral(token: string) {
    return this.service.referral.accept(token).then(
      action(() => {
        this.referralCode = null
      }),
    )
  }

  private acceptInvite(token: string) {
    return this.acceptInvitePromise.run(token)
  }
}

// TODO(FDN-7318): remove after refresh-failure investigation concludes.
// auth0-spa-js does not export these constants; copied verbatim from
// auth0-spa-js/src/cache/shared.ts so we can build the user-info key
const AUTH0_CACHE_KEY_PREFIX = '@@auth0spajs@@'
const AUTH0_USER_INFO_CACHE_SUFFIX = '@@user@@'

export class TrackingCache extends LocalStorageCache {
  constructor(private readonly clientId: string) {
    super()
  }

  public override set<T = Cacheable>(key: string, entry: T) {
    const body = (entry as { body?: { access_token?: string; refresh_token?: string } })
      ?.body
    try {
      if (body?.access_token) {
        const claims = this.getIdTokenClaims()

        trackCustomAction({
          type: 'universal-login-access-token-cache-set',
          hasRefreshToken: !!body.refresh_token,
          idTokenCacheFound: claims != null,
          userId: claims?.op_user_id ?? null,
          orgId: claims?.op_org_id ?? null,
          auth0UserId: claims?.sub ?? null,
        })

        if (
          !body.refresh_token &&
          window.localStorage.getItem(key)?.includes('"refresh_token"')
        ) {
          trackCustomAction({
            type: 'universal-login-refresh-token-cache-change',
            change: 'overwritten-without-refresh-token',
            key,
          })
        }
      }
    } catch (err) {
      logError(err)
    }
    try {
      return super.set(key, entry)
    } catch (err) {
      // Only meaningful when the failed write carried a refresh token: the
      // rotation succeeded server-side (e.g. quota exceeded on persist), so
      // the stored refresh token is now stale.
      if (body?.refresh_token) {
        trackCustomAction({
          type: 'universal-login-refresh-token-cache-change',
          change: 'write-failed',
          key,
        })
      }
      throw err
    }
  }

  public override remove(key: string) {
    try {
      if (window.localStorage.getItem(key)?.includes('"refresh_token"')) {
        trackCustomAction({
          type: 'universal-login-refresh-token-cache-change',
          change: 'removed',
          key,
        })
      }
    } catch (err) {
      logError(err)
    }
    return super.remove(key)
  }

  private getIdTokenClaims(): IdTokenClaims | undefined {
    const idTokenKey = `${AUTH0_CACHE_KEY_PREFIX}::${this.clientId}::${AUTH0_USER_INFO_CACHE_SUFFIX}`
    try {
      const raw = window.localStorage.getItem(idTokenKey)
      if (raw) {
        const parsed = JSON.parse(raw) as {
          decodedToken?: { claims?: IdTokenClaims }
        }
        return parsed?.decodedToken?.claims
      }
    } catch (error) {
      logError(error)
    }
  }
}

/**
 * Detects whether the current host is a worktree-prefixed dev subdomain
 * (e.g. `<worktree>.web.localhost`) rather than the plain dev host
 * (`web.localhost`, `localhost`, etc). Detection is purely structural (it
 * just counts hostname labels) — it isn't tied to any specific local dev
 * proxy tool, so it works with anything that assigns each worktree its own
 * `<name>.<base>.localhost` subdomain.
 *
 * When a worktree prefix is detected, returns the shared parent domain
 * (e.g. `.web.localhost`) that the Auth0 cache should scope its cookies to,
 * so logging in on one worktree subdomain shares the session with every
 * other worktree subdomain. Returns `null` when cookie sharing should not
 * apply (non-dev environments, or a host that isn't worktree-prefixed).
 */
export function getSharedAuthCookieDomain(
  hostname: string = window.location.hostname ?? '',
): string | null {
  if (config.ENV !== 'development') {
    return null
  }
  if (!hostname.endsWith('.localhost')) {
    return null
  }

  const labels = hostname.split('.')
  // A plain dev host is at most 2 labels (`web.localhost`) or 1
  // (`localhost`). A worktree-prefixed host has an extra leading label,
  // e.g. `fix-ui.web.localhost` (3 labels) — share cookies across the base
  // domain shared by all worktrees (the last two labels).
  if (labels.length < 3) {
    return null
  }

  return `.${labels.slice(-2).join('.')}`
}

// Browsers cap an individual cookie's total size (name + value + attributes)
// at ~4096 bytes. Auth0 cache entries bundle access/refresh/ID token data
// plus custom claims into a single JSON blob that can approach or exceed
// that limit, so large entries are split across multiple numbered cookies
// (`${key}.0`, `${key}.1`, ...) with a `${key}.chunks` cookie recording how
// many parts to reassemble. This threshold is kept well under 4096 to leave
// headroom for the (often long) Auth0 cache key used as the cookie name.
const SHARED_AUTH_COOKIE_CHUNK_SIZE = 3500
const SHARED_AUTH_COOKIE_CHUNK_COUNT_SUFFIX = '.chunks'

/**
 * Auth0 cache backed by cookies scoped to a shared parent domain (see
 * `getSharedAuthCookieDomain`), so a login performed on one worktree
 * subdomain is visible to every other worktree subdomain in dev. Falls back
 * to plain `TrackingCache`/`localStorage` behavior in every other
 * environment — this class is only ever constructed when
 * `getSharedAuthCookieDomain` returns a non-null domain.
 */
export class SubdomainSharedCache implements ICache {
  constructor(private readonly domain: string) {}

  public set<T = Cacheable>(key: string, entry: T): void {
    const serialized = JSON.stringify(entry)
    // Clear any previous representation (single cookie or chunked) before
    // writing, so switching between the two never leaves stale cookies.
    this.remove(key)

    // WrappedCacheEntry carries expiresAt (Unix epoch seconds). Use it so
    // cookies survive browser restarts, matching the localStorage behavior of
    // TrackingCache. KeyManifestEntry has no expiry and stays a session cookie.
    const options = this.cookieOptionsFor(entry)

    if (serialized.length <= SHARED_AUTH_COOKIE_CHUNK_SIZE) {
      Cookies.set(key, serialized, options)
      return
    }

    const chunks: string[] = []
    for (let i = 0; i < serialized.length; i += SHARED_AUTH_COOKIE_CHUNK_SIZE) {
      chunks.push(serialized.slice(i, i + SHARED_AUTH_COOKIE_CHUNK_SIZE))
    }
    chunks.forEach((chunk, index) => {
      Cookies.set(`${key}.${index}`, chunk, options)
    })
    Cookies.set(
      `${key}${SHARED_AUTH_COOKIE_CHUNK_COUNT_SUFFIX}`,
      String(chunks.length),
      options,
    )
  }

  public get<T = Cacheable>(key: string): T | undefined {
    const raw = this.readRaw(key)
    if (raw === undefined) {
      return undefined
    }
    try {
      return JSON.parse(raw) as T
    } catch (error) {
      logError(error)
      return undefined
    }
  }

  public remove(key: string): void {
    const chunkCount = this.readChunkCount(key)
    if (chunkCount != null) {
      for (let i = 0; i < chunkCount; i++) {
        Cookies.remove(`${key}.${i}`, this.removeOptions)
      }
      Cookies.remove(`${key}${SHARED_AUTH_COOKIE_CHUNK_COUNT_SUFFIX}`, this.removeOptions)
    }
    Cookies.remove(key, this.removeOptions)
  }

  public allKeys(): string[] {
    const keys = new Set<string>()
    for (const cookieKey of Object.keys(Cookies.get())) {
      if (!cookieKey.startsWith(AUTH0_CACHE_KEY_PREFIX)) {
        continue
      }
      if (cookieKey.endsWith(SHARED_AUTH_COOKIE_CHUNK_COUNT_SUFFIX)) {
        keys.add(cookieKey.slice(0, -SHARED_AUTH_COOKIE_CHUNK_COUNT_SUFFIX.length))
        continue
      }
      if (/\.\d+$/.test(cookieKey)) {
        continue // a chunk part, not a real key
      }
      keys.add(cookieKey)
    }
    return Array.from(keys)
  }

  // js-cookie's remove() only needs domain (and optionally path) to match the
  // Set-Cookie scope; it ignores expires.
  private get removeOptions(): Cookies.CookieAttributes {
    return { domain: this.domain }
  }

  private cookieOptionsFor<T>(entry: T): Cookies.CookieAttributes {
    // WrappedCacheEntry carries `expiresAt` (Unix epoch seconds). Convert it
    // to a Date so the cookie persists across browser restarts, matching the
    // localStorage-based TrackingCache behaviour. KeyManifestEntry has no
    // `expiresAt`, so those cookies remain session-scoped (they are
    // lightweight and trivially reconstructed by Auth0).
    const expiresAt = (entry as { expiresAt?: number }).expiresAt
    return {
      domain: this.domain,
      // Secure cookies require HTTPS; only opt in when actually served over
      // HTTPS so cookie writes don't silently fail on plain-http dev hosts.
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
      ...(expiresAt != null ? { expires: new Date(expiresAt * 1000) } : {}),
    }
  }

  private readRaw(key: string): string | undefined {
    const chunkCount = this.readChunkCount(key)
    if (chunkCount == null) {
      return Cookies.get(key)
    }

    const parts: string[] = []
    for (let i = 0; i < chunkCount; i++) {
      const part = Cookies.get(`${key}.${i}`)
      if (part === undefined) {
        return undefined // incomplete set — treat as a miss
      }
      parts.push(part)
    }
    return parts.join('')
  }

  private readChunkCount(key: string): number | null {
    const raw = Cookies.get(`${key}${SHARED_AUTH_COOKIE_CHUNK_COUNT_SUFFIX}`)
    if (raw === undefined) {
      return null
    }
    const count = Number(raw)
    return Number.isFinite(count) && count > 0 ? count : null
  }
}

export default UniversalLoginAuthStore
